import { Attribute, DBRow, FieldType } from '@cloudio-saas/datasource-types';
import { Platform } from 'componentregistry';
import Controller from 'controller/Controller';
import { isValid, parseISO } from 'date-fns';
import { camelCase, snakeCase, startCase } from 'lodash-es';
import Papa from 'papaparse';
import { getErrorMessage, isEmpty } from 'utils/isEmpty';

function processJSON(appUid: string, records: DBRow<any>[], skipWho = false) {
  let codes: string[] = [];
  const now = new Date().toISOString();
  if (!(Array.isArray(records) && records.length)) {
    throw Error('JSON must be an array of alteast one object!');
  }
  records = records.map(({ id, ...row }, index) => {
    return skipWho
      ? {
          id: id || index,
          ...row,
          _rs: 'Q',
        }
      : {
          id: id || index,
          createdBy: 'admin',
          lastUpdatedBy: 'admin',
          creationDate: now,
          lastUpdateDate: now,
          ...row,
          _rs: 'Q',
        };
  });
  const { _rs, ...others } = records[0];
  codes = Object.keys(others);

  const attrs = codes.map((attributeCode, index) => {
    const val = others[attributeCode];
    const value = typeof val === 'string' ? val.trim() : val;

    const attributeName = startCase(attributeCode);
    let maxLength = 0;
    let fieldType: FieldType = 'String';
    if (['Y', 'N'].includes(value)) {
      fieldType = 'YN';
      maxLength = 1;
    } else if (['T', 'F'].includes(value)) {
      fieldType = 'TF';
      maxLength = 1;
    } else if (
      typeof value === 'boolean' ||
      ['true', 'false'].includes(value)
    ) {
      fieldType = 'Boolean';
    } else if (typeof value === 'number') {
      const num = Number(value);
      fieldType = Number.isInteger(num) ? 'Integer' : 'Double';
      maxLength = 4;
    } else if (value && (Array.isArray(value) || typeof value === 'object')) {
      fieldType = 'JSON';
      maxLength = 9999;
    } else if (
      typeof value === 'string' &&
      value.charAt(4) === '-' &&
      value.charAt(7) === '-'
    ) {
      const date = parseISO(value);
      if (isValid(date)) {
        fieldType = value.length < 11 ? 'Date' : 'DateTime';
      } else {
        maxLength = Math.max(200, value.length);
      }
    }
    return {
      attributeCode,
      attributeType: fieldType,
      allowBlank: 'Y',
      columnName: attributeCode,
      appUid,
      attributeName,
      selectAllowed: 'Y',
      insertAllowed: 'Y',
      updateAllowed: 'Y',
      queryAllowed: 'Y',
      primaryKey: codes[index] === 'id' ? 'Y' : 'N',
      maxLength,
    } as Attribute;
  });
  return { records, attrs };
}

export function parseJSON(appUid: string, data: string, skipWho = false) {
  let records: DBRow<any>[] = [];
  records = JSON.parse(data.trim()) as DBRow<any>[];
  return processJSON(appUid, records, skipWho);
}

export async function parseTSData(props: {
  appUid: string;
  pageId: string;
  itemId: string;
  data: string;
  skipWho?: boolean;
  platform: Platform;
}) {
  const { appUid, pageId, itemId, data, skipWho = false, platform } = props;
  let records: DBRow<any>[] = [];

  try {
    await Controller.compile({
      appUid,
      pageId: `data_${appUid}_${pageId}_${itemId}`,
      platform,
      source: data,
      fileName: `data_${appUid}_${pageId}_${itemId}.tsx`,
      debug: platform.isDeveloper(appUid),
    });
    const returnValue = await Controller.callAsyncIfExists({
      appUid,
      itemId,
      pageId: `data_${appUid}_${pageId}_${itemId}`,
      platform,
      functionName: 'getData',
      props: {},
    });
    if (returnValue.status === 'NONE') {
      platform.showError('Missing function getData!');
    }
    if (returnValue.status === 'SUCCESS') {
      if (!Array.isArray(returnValue.result)) {
        platform.showError('getData function must return an array of objects');
      }
      records = returnValue.result as DBRow<any>[];
    }
  } catch (e) {
    platform.showError(`Error compiling app controller: ${getErrorMessage(e)}`);
  }

  return processJSON(appUid, records, skipWho);
}

export function parseCSV({
  platform,
  appUid,
  pageId,
  itemId,
  data,
}: {
  platform: Platform;
  appUid: string;
  pageId: string;
  itemId: string;
  data: string;
}) {
  let headers: string[] = [];
  let codes: string[] = [];
  let rowOne: string[] = [];
  let records: DBRow<any>[] = [];
  let csvRows: string[][] = [];

  const csv = Papa.parse<string[]>(data.trim(), {
    delimiter: ',',
    header: false,
  });
  if (csv.errors.length) {
    platform.showError(
      csv.errors
        .map(({ message, row }) => `${message} at row ${row}`)
        .join('\n'),
      { appUid, pageId, itemId },
    );
  }
  const [headersX, ...rows] = csv.data;
  headers = headersX;
  codes = headers.map((h) => camelCase(h));
  rowOne = rows[0];
  csvRows = rows;

  if (!codes.includes('id')) {
    codes.push('id');
    headers.push('ID');
    rowOne.push(`${rows.length}`);
  }

  const attrs = rowOne.map((val, index) => {
    const value = typeof val === 'string' ? val.trim() : val;

    let fieldType: FieldType = 'String';
    if (['Y', 'N'].includes(value)) {
      fieldType = 'YN';
    } else if (['T', 'F'].includes(value)) {
      fieldType = 'TF';
    } else if (
      typeof value === 'boolean' ||
      ['true', 'false'].includes(value)
    ) {
      fieldType = 'Boolean';
    } else if (typeof value === 'number' || !Number.isNaN(Number(value))) {
      const num = Number(value);
      fieldType = Number.isInteger(num) ? 'Integer' : 'Double';
    } else if (
      typeof value === 'string' &&
      value.charAt(4) === '-' &&
      value.charAt(7) === '-'
    ) {
      const date = parseISO(value);
      if (isValid(date)) {
        fieldType = value.length < 11 ? 'Date' : 'DateTime';
      }
    }
    return {
      attributeCode: codes[index],
      attributeType: fieldType,
      allowBlank: 'Y',
      columnName: snakeCase(headers[index]).toUpperCase(),
      appUid,
      attributeName: headers[index],
      selectAllowed: 'Y',
      insertAllowed: 'Y',
      updateAllowed: 'Y',
      queryAllowed: 'Y',
      primaryKey: codes[index] === 'id' ? 'Y' : 'N',
    } as Attribute;
  });

  records = csvRows.map((row, index) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rec: DBRow<any> = { id: index, _rs: 'Q' as const };
    attrs.forEach((attr, cidx) => {
      const val = row[cidx];
      if (isEmpty(val)) {
        return;
      }
      const { attributeType, attributeCode } = attr;
      switch (attributeType) {
        case 'Boolean':
          rec[attributeCode] = val !== 'false';
          break;
        case 'Integer':
        case 'Double':
          rec[attributeCode] = Number(val);
          break;
        default:
          rec[attributeCode] = val;
      }
    });
    return rec;
  });

  return { records, attrs };
}
